import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ContactWidget from '../../components/ui/ContactWidget';
import ImageGrid from './components/ImageGrid';
import LightboxViewer from './components/LightboxViewer';
import SkeletonLoader from './components/SkeletonLoader';
import InspiredCTA from './components/InspiredCTA';
import Icon from '../../components/AppIcon';

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [viewedCount, setViewedCount] = useState(0);
  const [showInspiredCTA, setShowInspiredCTA] = useState(false);
  const [loadedImagesCount, setLoadedImagesCount] = useState(16);

  // --- FIX 2: RESTORED FILTERS ---
  // The filters array is now populated with all relevant categories.
  const filters = [
    { id: 'all', label: 'All Events' },
    { id: 'weddings', label: 'Weddings' },
    { id: 'corporate', label: 'Corporate' },
    { id: 'private', label: 'Private' },
    { id: 'entertainment', label: 'Entertainment' },
    { id: 'catering', label: 'Catering' }
  ];

  // --- FIX 3: RESTORED IMAGE CATEGORIES ---
  // The 'category' property has been added to each image object.
  const allImages = [
    // Weddings (15 images)
    { id: 1, src: "/gallery/1.jpg", alt: "Elegant wedding ceremony with floral arch", category: 'weddings' },
    { id: 2, src: "/gallery/2.jpg", alt: "Bride and groom sharing a happy moment", category: 'weddings' },
    { id: 3, src: "/gallery/3.jpg", alt: "Beautifully decorated reception tables", category: 'weddings' },
    { id: 4, src: "/gallery/4.jpg", alt: "Outdoor wedding venue under fairy lights", category: 'weddings' },
    { id: 5, src: "/gallery/5.jpg", alt: "Stunning multi-tiered wedding cake", category: 'weddings' },
    { id: 6, src: "/gallery/6.jpg", alt: "Close-up of the bridal bouquet", category: 'weddings' },
    { id: 7, src: "/gallery/7.jpg", alt: "Guests dancing at the wedding reception", category: 'weddings' },
    { id: 8, src: "/gallery/8.jpg", alt: "Wedding rings on a velvet cushion", category: 'weddings' },
    { id: 9, src: "/gallery/9.jpg", alt: "Ceremony aisle lined with flower petals", category: 'weddings' },
    { id: 10, src: "/gallery/10.jpg", alt: "Groom looking at the bride during vows", category: 'weddings' },
    { id: 11, src: "/gallery/11.jpg", alt: "Sunset portrait of the newlywed couple", category: 'weddings' },
    { id: 12, src: "/gallery/12.jpg", alt: "Bridesmaids in matching elegant dresses", category: 'weddings' },
    { id: 13, src: "/gallery/13.jpg", alt: "Sparkler send-off for the happy couple", category: 'weddings' },
    { id: 14, src: "/gallery/14.jpg", alt: "Intricate details of the wedding invitation suite", category: 'weddings' },
    { id: 15, src: "/gallery/15.jpg", alt: "A toast during the wedding speeches", category: 'weddings' },

    // Corporate (10 images)
    { id: 16, src: "/gallery/16.jpg", alt: "Keynote speaker on stage at a conference", category: 'corporate' },
    { id: 17, src: "/gallery/17.jpg", alt: "Audience engaged in a business seminar", category: 'corporate' },
    { id: 18, src: "/gallery/18.jpg", alt: "Networking session during a corporate event", category: 'corporate' },
    { id: 19, src: "/gallery/19.jpg", alt: "Professionally arranged gala dinner tables", category: 'corporate' },
    { id: 20, src: "/gallery/20.jpg", alt: "Product launch event with brand logos", category: 'corporate' },
    { id: 21, src: "/gallery/21.jpg", alt: "Team-building activity outdoors", category: 'corporate' },
    { id: 22, src: "/gallery/22.jpg", alt: "Modern audio-visual setup for a presentation", category: 'corporate' },
    { id: 23, src: "/gallery/23.jpg", alt: "Panel discussion with industry experts", category: 'corporate' },
    { id: 24, src: "/gallery/24.jpg", alt: "Awards ceremony with trophies on display", category: 'corporate' },
    { id: 25, src: "/gallery/25.jpg", alt: "Corporate guests enjoying a coffee break", category: 'corporate' },

    // Private Celebrations (10 images)
    { id: 26, src: "/gallery/26.jpg", alt: "Vibrant decorations for a birthday party", category: 'private' },
    { id: 27, src: "/gallery/27.jpg", alt: "Elegant table setting for an anniversary dinner", category: 'private' },
    { id: 28, src: "/gallery/28.jpg", alt: "Joyful family at a graduation celebration", category: 'private' },
    { id: 29, src: "/gallery/29.jpg", alt: "Stylish decorations for a baby shower", category: 'private' },
    { id: 30, src: "/gallery/30.jpg", alt: "Friends celebrating at a garden party", category: 'private' },
    { id: 31, src: "/gallery/31.jpg", alt: "A milestone birthday cake with candles", category: 'private' },
    { id: 32, src: "/gallery/32.jpg", alt: "Intimate setting for an engagement party", category: 'private' },
    { id: 33, src: "/gallery/33.jpg", alt: "Guests mingling at a family reunion", category: 'private' },
    { id: 34, src: "/gallery/34.jpg", alt: "Themed decor for a holiday party", category: 'private' },
    { id: 35, src: "/gallery/35.jpg", alt: "Fun photo booth props at a celebration", category: 'private' },

    // Entertainment (5 images)
    { id: 36, src: "/gallery/36.jpg", alt: "Live band performing on a lit stage", category: 'entertainment' },
    { id: 37, src: "/gallery/37.jpg", alt: "DJ at the decks with a dancing crowd", category: 'entertainment' },
    { id: 38, src: "/gallery/38.jpg", alt: "String quartet playing at an elegant event", category: 'entertainment' },
    { id: 39, src: "/gallery/39.jpg", alt: "Magician performing for guests", category: 'entertainment' },
    { id: 40, src: "/gallery/40.jpg", alt: "Fireworks display at the end of the night", category: 'entertainment' },

    // Catering (10 images)
    { id: 41, src: "/gallery/41.jpg", alt: "Gourmet appetizers arranged on a platter", category: 'catering' },
    { id: 42, src: "/gallery/42.jpg", alt: "Elaborate buffet spread with various dishes", category: 'catering' },
    { id: 43, src: "/gallery/43.jpg", alt: "Chef plating a main course with precision", category: 'catering' },
    { id: 44, src: "/gallery/44.jpg", alt: "Colorful cocktails at a mobile bar station", category: 'catering' },
    { id: 45, src: "/gallery/45.jpg", alt: "Decadent dessert table with cakes and pastries", category: 'catering' },
    { id: 46, src: "/gallery/46.jpg", alt: "Artfully presented canapés", category: 'catering' },
    { id: 47, src: "/gallery/47.jpg", alt: "Fresh seafood bar on ice", category: 'catering' },
    { id: 48, src: "/gallery/48.jpg", alt: "Waitstaff serving champagne to guests", category: 'catering' },
    { id: 49, src: "/gallery/49.jpg", alt: "Interactive food station, like a taco bar", category: 'catering' },
    { id: 50, src: "/gallery/50.jpg", alt: "Elegant place setting with fine china", category: 'catering' }
  ];

  useEffect(() => {
    const timer = setTimeout(() => { setLoading(false); }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sourceImages = activeFilter === 'all'
      ? allImages
      : allImages.filter(img => img.category === activeFilter);
    setFilteredImages(sourceImages.slice(0, loadedImagesCount));
  }, [activeFilter, loadedImagesCount]);

  useEffect(() => {
    if (viewedCount >= 10 && !showInspiredCTA) { setShowInspiredCTA(true); }
  }, [viewedCount, showInspiredCTA]);

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setLoadedImagesCount(16);
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    setViewedCount(prev => prev + 1);
  };

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev + 1) % filteredImages.length);
  }, [filteredImages.length]);

  const handlePreviousImage = useCallback(() => {
    setCurrentImageIndex(prev => (prev - 1 + filteredImages.length) % filteredImages.length);
  }, [filteredImages.length]);

  const handleToggleFavorite = (imageId) => {
    setFavorites(prev =>
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const handleShare = async (image) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Luxe Allure Wedding Gallery',
          text: image.alt,
          url: window.location.href
        });
      } catch (error) { console.error('Error sharing:', error); }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleLoadMore = useCallback(() => {
    setLoadedImagesCount(prev => Math.min(prev + 8, allImages.length));
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 500) {
        const sourceImages = activeFilter === 'all'
          ? allImages
          : allImages.filter(img => img.category === activeFilter);
        if(loadedImagesCount < sourceImages.length) {
          handleLoadMore();
        }
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleLoadMore, activeFilter, loadedImagesCount]);

  return (
    <>
      <Helmet>
        <title>Gallery - Luxe Allure Wedding | Event Photography Showcase</title>
        <meta name="description" content="Browse our stunning gallery of wedding and event photography. Get inspired by our premium event planning and coordination services." />
        <meta name="keywords" content="wedding gallery, event photography, wedding photos, corporate events, private celebrations" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
        <Header />
        <ContactWidget />
        
        <main className="pt-20">
          {/* --- THIS IS THE FIX --- */}
          {/* Changed `py-8` to `pt-8 pb-32` to add extra space at the bottom */}
          <div className="container mx-auto px-6 lg:px-8 pt-8 pb-32">
            <Breadcrumb />
            
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-foreground mb-4">
                Our Gallery
              </h1>
              <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
                Discover the magic we create through our lens. Each image tells a story of love, celebration, and unforgettable moments.
              </p>
            </div>

           

            {loading ? (
              <SkeletonLoader count={16} />
            ) : (
              <>
                <ImageGrid 
                  images={filteredImages}
                  onImageClick={handleImageClick}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
                
                {loadedImagesCount < (activeFilter === 'all' ? allImages.length : allImages.filter(img => img.category === activeFilter).length) && (
                  <div className="text-center mt-12">
                    <div className="inline-flex items-center space-x-2 text-muted-foreground">
                      <Icon name="Loader" size={16} className="animate-spin" />
                      <span className="font-body text-sm">Loading more images...</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && filteredImages.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Image" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                  No images found
                </h3>
                <p className="font-body text-muted-foreground">
                  Try selecting a different category to view more images.
                </p>
              </div>
            )}
          </div>
        </main>

        <LightboxViewer 
          images={filteredImages}
          currentIndex={currentImageIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
          onShare={handleShare}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
        />

        <InspiredCTA 
          isVisible={showInspiredCTA}
          onClose={() => setShowInspiredCTA(false)}
        />
      </div>
    </>
  );
};

export default Gallery;